import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import PollenDotsNav from '@/components/PollenDotsNav';
import { API, useAuth } from '@/App';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Loader2, CreditCard, Receipt, Trash2 } from 'lucide-react';
import { formatDate } from '@/lib/dateUtils';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import RazorpayPanel from '@/components/RazorpayPanel';
import DocumentsPanel from '@/components/DocumentsPanel';

const catColors = { Hotel: 'bg-emerald-100 text-emerald-800', Transport: 'bg-amber-100 text-amber-800', Activity: 'bg-blue-100 text-blue-800', Agent: 'bg-purple-100 text-purple-800', Guide: 'bg-teal-100 text-teal-800' };
export const PD_ACCOUNTS = ['IDFC First Bank', 'Axis Bank', 'Razorpay', 'RazorpayX', 'Petty Cash', "Soumen's Personal", 'Undeposited Fund', 'Cash', 'UPI', 'Bank Remittance'];

const PollenDotsPayments = () => {
  const POLLEN_API = `${API}/pollen`;
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('payments');
  const [dialogType, setDialogType] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {type: 'payment'|'expense', id: '...'}
  const [accounts, setAccounts] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [showTransfer, setShowTransfer] = useState(false);
  const [tForm, setTForm] = useState({});

  const authFetch = (url, opts = {}) => {
    const h = {}; const t = localStorage.getItem('session_token'); if (t) h['Authorization'] = `Bearer ${t}`;
    return fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...h } });
  };

  const fetchData = useCallback(async () => {
    try {
      const [pRes, eRes, tRes, aRes, trRes] = await Promise.all([
        authFetch(`${POLLEN_API}/payments`), authFetch(`${POLLEN_API}/expenses`), authFetch(`${POLLEN_API}/tours`), authFetch(`${POLLEN_API}/accounts-summary`), authFetch(`${POLLEN_API}/account-transfers`)
      ]);
      if (pRes.ok) setPayments(await pRes.json());
      if (eRes.ok) setExpenses(await eRes.json());
      if (tRes.ok) setTours(await tRes.json());
      if (aRes.ok) setAccounts(await aRes.json());
      if (trRes.ok) setTransfers(await trRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tourMap = Object.fromEntries(tours.map(t => [t.id, t.tour_name]));

  const handleDeleteItem = async () => {
    if (!deleteTarget) return;
    const { type, id } = deleteTarget;
    await authFetch(`${POLLEN_API}/${type}s/${id}`, { method: 'DELETE' });
    toast.success(`${type === 'payment' ? 'Payment' : 'Expense'} deleted`);
    setDeleteTarget(null);
    fetchData();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const isPayment = dialogType === 'payment';
    const url = isPayment ? `${POLLEN_API}/payments` : `${POLLEN_API}/expenses`;
    const payload = isPayment
      ? { tour_id: form.tour_id || '', vendor_name: form.vendor_name || '', category: form.category || '', amount: parseFloat(form.amount) || 0, date: form.date || '', notes: form.notes || '', paid_from: form.paid_from || '' }
      : { tour_id: form.tour_id || '', category: form.category || '', description: form.description || '', amount: parseFloat(form.amount) || 0, date: form.date || '', paid_from: form.paid_from || '' };
    try {
      const res = await authFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { toast.success(`${isPayment ? 'Payment' : 'Expense'} added`); setDialogType(null); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Error'); }
    setSubmitting(false);
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    const res = await authFetch(`${POLLEN_API}/account-transfers`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ from_account: tForm.from_account || '', to_account: tForm.to_account || '', amount: parseFloat(tForm.amount) || 0, date: tForm.date || '', notes: tForm.notes || '' })
    });
    if (res.ok) { toast.success('Transfer recorded'); setShowTransfer(false); setTForm({}); fetchData(); }
    else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Failed'); }
  };

  if (!['Pollen Dots User','Super Admin','Admin','Operator','Intern'].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  // Payments restricted to profit-viewing roles
  if (!['Pollen Dots User','Super Admin','Admin'].includes(user?.role)) return <Navigate to="/pollen-dots-dashboard" replace />;
  if (loading) return <Layout><div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  const totalPayments = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <Layout>
      <div className="space-y-4" data-testid="payments-page">
        <PollenDotsNav />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Payments & Expenses</h1>
          <div className="flex gap-2">
            <Button onClick={() => { setDialogType('payment'); setForm({ date: new Date().toISOString().slice(0, 10) }); }} data-testid="add-payment-btn"><CreditCard className="w-4 h-4 mr-1" />Add Payment</Button>
            <Button variant="outline" onClick={() => { setDialogType('expense'); setForm({ date: new Date().toISOString().slice(0, 10) }); }} data-testid="add-expense-btn"><Receipt className="w-4 h-4 mr-1" />Add Expense</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Card><CardContent className="pt-4 pb-3"><p className="text-xs text-muted-foreground">Total Payments</p><p className="text-xl font-bold text-emerald-600">{totalPayments.toLocaleString('en-IN')}</p></CardContent></Card>
          <Card><CardContent className="pt-4 pb-3"><p className="text-xs text-muted-foreground">Total Expenses</p><p className="text-xl font-bold text-red-600">{totalExpenses.toLocaleString('en-IN')}</p></CardContent></Card>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList><TabsTrigger value="payments">Payments ({payments.length})</TabsTrigger><TabsTrigger value="expenses">Expenses ({expenses.length})</TabsTrigger><TabsTrigger value="razorpay" data-testid="razorpay-tab">Razorpay</TabsTrigger><TabsTrigger value="documents" data-testid="documents-tab">Documents</TabsTrigger><TabsTrigger value="accounts" data-testid="accounts-tab">Accounts</TabsTrigger></TabsList>
          <TabsContent value="accounts" className="mt-4 space-y-4">
            <Button size="sm" onClick={() => { setShowTransfer(true); setTForm({ date: new Date().toISOString().slice(0, 10) }); }} data-testid="new-transfer-btn"><Plus className="w-4 h-4 mr-1" />Record Transfer</Button>
            {accounts.length === 0 ? <Card><CardContent className="py-8 text-center text-muted-foreground">No account activity yet — pick a "Paid from" account on payments/expenses and a "Received into" account on receipts.</CardContent></Card> : (
              <div className="overflow-x-auto" data-testid="accounts-summary-table"><Table>
                <TableHeader><TableRow>
                  <TableHead>Account</TableHead><TableHead className="text-right">Money In</TableHead>
                  <TableHead className="text-right">Money Out</TableHead><TableHead className="text-right">Net</TableHead>
                  <TableHead className="text-right">Entries</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {accounts.map(a => (
                    <TableRow key={a.account}>
                      <TableCell className="font-medium">{a.account}</TableCell>
                      <TableCell className="text-right text-emerald-600">₹{a.money_in.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-right text-red-600">₹{a.money_out.toLocaleString('en-IN')}</TableCell>
                      <TableCell className={`text-right font-bold ${a.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>₹{a.net.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-right text-muted-foreground text-sm">{a.in_count + a.out_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></div>
            )}
            {transfers.length > 0 && (
              <Card><CardContent className="pt-4">
                <p className="text-sm font-semibold mb-2">Transfers ({transfers.length})</p>
                <div className="overflow-x-auto"><Table>
                  <TableHeader><TableRow><TableHead>From</TableHead><TableHead>To</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Date</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {transfers.map(t => (
                      <TableRow key={t.id}>
                        <TableCell>{t.from_account}</TableCell><TableCell>{t.to_account}</TableCell>
                        <TableCell className="text-right font-medium">₹{t.amount?.toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{t.date}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{t.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></div>
              </CardContent></Card>
            )}
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            <DocumentsPanel />
          </TabsContent>
          <TabsContent value="razorpay" className="mt-4">
            <RazorpayPanel tours={tours} />
          </TabsContent>
          <TabsContent value="payments" className="mt-4">
            {payments.length === 0 ? <Card><CardContent className="py-8 text-center text-muted-foreground">No payments recorded</CardContent></Card> : (
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow>
                  <TableHead>Tour</TableHead><TableHead>Vendor</TableHead><TableHead>Category</TableHead>
                  <TableHead className="text-right">Amount</TableHead><TableHead>Paid From</TableHead><TableHead>Date</TableHead><TableHead>Notes</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {payments.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-muted-foreground text-sm">{tourMap[p.tour_id] || '-'}</TableCell>
                      <TableCell className="font-medium">{p.vendor_name || '-'}</TableCell>
                      <TableCell>{p.category ? <Badge className={catColors[p.category]}>{p.category}</Badge> : '-'}</TableCell>
                      <TableCell className="text-right font-medium text-emerald-600">{p.amount?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{p.paid_from || '-'}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{p.date}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{p.notes || '-'}</TableCell>
                      <TableCell><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => setDeleteTarget({ type: 'payment', id: p.id })}><Trash2 className="w-3.5 h-3.5" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></div>
            )}
          </TabsContent>
          <TabsContent value="expenses" className="mt-4">
            {expenses.length === 0 ? <Card><CardContent className="py-8 text-center text-muted-foreground">No expenses recorded</CardContent></Card> : (
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow>
                  <TableHead>Tour</TableHead><TableHead>Category</TableHead><TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead><TableHead>Paid From</TableHead><TableHead>Date</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {expenses.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-muted-foreground text-sm">{tourMap[e.tour_id] || '-'}</TableCell>
                      <TableCell className="font-medium">{e.category || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{e.description || '-'}</TableCell>
                      <TableCell className="text-right font-medium text-red-600">{e.amount?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{e.paid_from || '-'}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(e.date)}</TableCell>
                      <TableCell><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => setDeleteTarget({ type: 'expense', id: e.id })}><Trash2 className="w-3.5 h-3.5" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></div>
            )}
          </TabsContent>
        </Tabs>

        <Dialog open={!!dialogType} onOpenChange={(open) => { if (!open) setDialogType(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Add {dialogType === 'payment' ? 'Payment' : 'Expense'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 mt-2">
              <div><Label>Tour</Label>
                <Select value={form.tour_id || ''} onValueChange={v => setForm({...form, tour_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select tour" /></SelectTrigger>
                  <SelectContent>{tours.map(t => <SelectItem key={t.id} value={t.id}>{t.tour_name}</SelectItem>)}</SelectContent>
                </Select></div>
              {dialogType === 'payment' && <>
                <div><Label>Vendor Name</Label><Input value={form.vendor_name || ''} onChange={e => setForm({...form, vendor_name: e.target.value})} /></div>
                <div><Label>Category</Label>
                  <Select value={form.category || ''} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent>{['Hotel', 'Transport', 'Activity', 'Agent'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select></div>
              </>}
              {dialogType === 'expense' && <>
                <div><Label>Category *</Label><Input value={form.category || ''} onChange={e => setForm({...form, category: e.target.value})} required /></div>
                <div><Label>Description</Label><Input value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} /></div>
              </>}
              <div><Label>Amount *</Label><Input type="number" step="0.01" value={form.amount || ''} onChange={e => setForm({...form, amount: e.target.value})} required /></div>
              <div><Label>Paid from (account)</Label>
                <Select value={form.paid_from || ''} onValueChange={v => setForm({...form, paid_from: v})}>
                  <SelectTrigger data-testid="paid-from-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>{PD_ACCOUNTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Date *</Label><Input type="date" value={form.date || ''} onChange={e => setForm({...form, date: e.target.value})} required /></div>
              {dialogType === 'payment' && <div><Label>Notes</Label><Input value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})} /></div>}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogType(null)}>Cancel</Button>
                <Button type="submit" disabled={submitting}>{submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Add</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        <Dialog open={showTransfer} onOpenChange={(o) => { if (!o) setShowTransfer(false); }}>
          <DialogContent className="max-w-sm" data-testid="transfer-dialog">
            <DialogHeader><DialogTitle>Record Account Transfer</DialogTitle></DialogHeader>
            <form onSubmit={submitTransfer} className="space-y-3 mt-2">
              <div><Label>From account *</Label>
                <Select value={tForm.from_account || ''} onValueChange={v => setTForm({...tForm, from_account: v})}>
                  <SelectTrigger data-testid="transfer-from-select"><SelectValue placeholder="From" /></SelectTrigger>
                  <SelectContent>{PD_ACCOUNTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>To account *</Label>
                <Select value={tForm.to_account || ''} onValueChange={v => setTForm({...tForm, to_account: v})}>
                  <SelectTrigger data-testid="transfer-to-select"><SelectValue placeholder="To" /></SelectTrigger>
                  <SelectContent>{PD_ACCOUNTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Amount *</Label><Input data-testid="transfer-amount-input" type="number" step="0.01" min="0.01" value={tForm.amount || ''} onChange={e => setTForm({...tForm, amount: e.target.value})} required /></div>
              <div><Label>Date</Label><Input type="date" value={tForm.date || ''} onChange={e => setTForm({...tForm, date: e.target.value})} /></div>
              <div><Label>Notes</Label><Input value={tForm.notes || ''} onChange={e => setTForm({...tForm, notes: e.target.value})} placeholder="e.g. Razorpay settlement" /></div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowTransfer(false)}>Cancel</Button>
                <Button type="submit" data-testid="transfer-submit-btn">Record</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDeleteItem}
          title={`Delete ${deleteTarget?.type === 'payment' ? 'Payment' : 'Expense'}`} description="This will permanently remove this record." />
      </div>
    </Layout>
  );
};

export default PollenDotsPayments;
